/**
 * File Validation Utilities
 *
 * Centralized file validation system that consolidates duplicate validation logic
 * from FileUpload.tsx, stepValidation.ts, and ModernFileUpload.tsx.
 *
 * Provides comprehensive file validation with proper error handling, i18n support,
 * and consistent validation rules across the application.
 *
 * @example Basic file validation
 * ```typescript
 * import { validateFile } from '../../utils/fileValidation';
 *
 * const result = validateFile(file);
 * if (!result.isValid) {
 *   showValidationErrors(result.errors);
 * }
 * ```
 *
 * @example Multiple files validation
 * ```typescript
 * import { validateMultipleFiles } from '../../utils/fileValidation';
 *
 * const result = validateMultipleFiles(files);
 * if (result.isValid) {
 *   handleValidFiles(result.validFiles);
 * }
 * ```
 *
 * @example Custom validation options
 * ```typescript
 * import { validateFile, FileValidationOptions } from '../../utils/fileValidation';
 *
 * const options: FileValidationOptions = {
 *   maxFileSize: 10 * 1024 * 1024, // 10MB
 *   maxFileCount: 5
 * };
 * const result = validateFile(file, options);
 * ```
 */

import { TASK_CONSTANTS } from '../types';
import {
  FileValidationResult,
  FileValidationError,
  FileValidationErrorType,
  FileValidationOptions,
  SupportedFileType,
  FileSizeFormatOptions,
} from '../types/fileValidation';

/**
 * Default file validation configuration
 * Extends TASK_CONSTANTS with additional validation rules
 */
export const FILE_VALIDATION_CONFIG = {
  MAX_FILE_SIZE: TASK_CONSTANTS.MAX_FILE_SIZE, // 25MB
  MAX_TOTAL_SIZE: 250 * 1024 * 1024, // 250MB — primary guardrail (no per-task count limit)
  ALLOWED_TYPES: [
    ...TASK_CONSTANTS.SUPPORTED_FILE_TYPES,
  ] as SupportedFileType[],
  ALLOW_EMPTY_FILES: false,
} as const;

/**
 * File size formatting utility
 * Formats bytes into human-readable format (e.g., "5.2 MB")
 *
 * @param bytes - Number of bytes to format
 * @param options - Formatting options
 * @returns Formatted file size string
 */
export const formatFileSize = (
  bytes: number,
  options: FileSizeFormatOptions = {}
): string => {
  const { precision = 2, units = 'binary', includeUnit = true } = options;

  if (bytes === 0) return includeUnit ? '0 Bytes' : '0';

  const base = units === 'binary' ? 1024 : 1000;
  const unitLabels =
    units === 'binary'
      ? ['Bytes', 'KB', 'MB', 'GB', 'TB']
      : ['Bytes', 'kB', 'MB', 'GB', 'TB'];

  const unitIndex = Math.floor(Math.log(bytes) / Math.log(base));
  const size = bytes / Math.pow(base, unitIndex);
  const formattedSize =
    unitIndex === 0 ? size.toString() : size.toFixed(precision);

  return includeUnit
    ? `${formattedSize} ${unitLabels[unitIndex]}`
    : formattedSize;
};

/**
 * Creates a standardized file validation error
 *
 * @param type - Error type
 * @param message - Human-readable error message
 * @param fileName - Optional file name
 * @param metadata - Additional error metadata
 * @returns FileValidationError object
 */
const createValidationError = (
  type: FileValidationErrorType,
  message: string,
  fileName?: string,
  metadata: Partial<FileValidationError> = {}
): FileValidationError => ({
  type,
  message,
  fileName,
  code: `fileValidation.${type.toLowerCase()}`,
  ...metadata,
});

/**
 * Validates a single file against validation rules
 *
 * @param file - File to validate
 * @param options - Validation options (optional)
 * @returns File validation result
 */
export const validateFile = (
  file: File,
  options: FileValidationOptions = {}
): FileValidationResult => {
  const config = {
    maxFileSize: options.maxFileSize ?? FILE_VALIDATION_CONFIG.MAX_FILE_SIZE,
    allowedTypes: options.allowedTypes ?? FILE_VALIDATION_CONFIG.ALLOWED_TYPES,
    allowEmptyFiles:
      options.allowEmptyFiles ?? FILE_VALIDATION_CONFIG.ALLOW_EMPTY_FILES,
  };

  const errors: FileValidationError[] = [];
  const warnings: string[] = [];

  // Validate file exists and is a valid File object
  if (!file || !(file instanceof File)) {
    const fileName =
      file && typeof file === 'object' && 'name' in file
        ? String((file as { name: unknown }).name)
        : undefined;

    errors.push(
      createValidationError(
        FileValidationErrorType.INVALID_FILE,
        'Invalid file object',
        fileName
      )
    );
    return {
      isValid: false,
      errors,
      warnings,
      validFiles: [],
      totalSize: 0,
    };
  }

  // Check if file is empty
  if (file.size === 0 && !config.allowEmptyFiles) {
    errors.push(
      createValidationError(
        FileValidationErrorType.EMPTY_FILE,
        `File "${file.name}" is empty`,
        file.name,
        { actualSize: file.size }
      )
    );
  }

  // Validate file size
  const maxSize = options.customSizeLimit
    ? options.customSizeLimit(file)
    : config.maxFileSize;
  if (file.size > maxSize) {
    errors.push(
      createValidationError(
        FileValidationErrorType.SIZE_EXCEEDED,
        `File "${file.name}" exceeds size limit (${formatFileSize(file.size)} > ${formatFileSize(maxSize)})`,
        file.name,
        {
          actualSize: file.size,
          maxSize: maxSize,
        }
      )
    );
  }

  // Validate file type
  const isTypeValid = options.customTypeCheck
    ? options.customTypeCheck(file)
    : config.allowedTypes.includes(file.type as SupportedFileType);

  if (!isTypeValid) {
    errors.push(
      createValidationError(
        FileValidationErrorType.UNSUPPORTED_TYPE,
        `File type "${file.type}" is not supported for file "${file.name}"`,
        file.name,
        { actualType: file.type }
      )
    );
  }

  // Add warnings for large files (>1MB but within limits)
  if (file.size > 1024 * 1024 && file.size <= maxSize) {
    warnings.push(`Large file: ${file.name} (${formatFileSize(file.size)})`);
  }

  const isValid = errors.length === 0;

  return {
    isValid,
    errors,
    warnings,
    validFiles: isValid ? [file] : [],
    totalSize: file.size,
  };
};

/**
 * Gets file extension from filename
 *
 * @param fileName - Name of the file
 * @returns File extension (lowercase) or empty string
 */
export const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex !== -1
    ? fileName.slice(lastDotIndex + 1).toLowerCase()
    : '';
};

/**
 * Checks if a file is an image based on MIME type
 *
 * @param file - File to check
 * @returns True if file is an image
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};
